// FILE: assets/bold-file-upload-ui.js
// Purpose: DOM-only UI wrapper for Bold file uploads.
// - Mounts the “attachment chip” inside the closest .bold_option_uploadfile container
//   (avoids label/parent click handlers that open the file picker).
// - Hides the native .bold_option_element while a file is selected.
// - Provides a Remove action that clears the input and updates the facade file flag.

(function () {
  if (window.BoldFileUploadUI) return;

  const SELECTORS = {
    fileInput: '.bold_option_uploadfile .bold_option_element input[type="file"]',
    optionElement: '.bold_option_element',
    wrapper: '.bold_option_uploadfile',
  };

  const CLASS = {
    hasFile: 'bold-file-has-file',
    chip: 'bold-file-chip',
    chipActive: 'is-active',
  };

  let _observer = null;
  let _scanScheduled = false;

  function _getFacade() {
    const adapter = document.querySelector('bold-sc-adapter');
    return adapter && adapter.facade ? adapter.facade : null;
  }

  function _getLabelFromInput(input) {
    if (!input || !input.name) return null;
    const m = input.name.match(/properties\[(.*?)\]/);
    return m && m[1] ? m[1] : null;
  }

  function _revokeObjectUrl(imgEl) {
    if (!imgEl) return;
    const url = imgEl.dataset.objectUrl;
    if (url) {
      try {
        URL.revokeObjectURL(url);
      } catch (e) {}
      delete imgEl.dataset.objectUrl;
    }
  }

  function _getWrapper(input) {
    return input.closest(SELECTORS.wrapper);
  }

  function _ensureChip(input) {
    const wrapper = _getWrapper(input);
    if (!wrapper) return null;

    // chip is a direct child of .bold_option_uploadfile
    let chip = wrapper.querySelector(`:scope > .${CLASS.chip}`);
    if (chip) return chip;

    chip = document.createElement('div');
    chip.className = CLASS.chip;
    chip.setAttribute('aria-live', 'polite');
    chip.innerHTML = `
      <img class="bold-file-chip__thumb" alt="" />
      <div class="bold-file-chip__meta">
        <div class="bold-file-chip__name" title=""></div>
        <button type="button" class="bold-file-chip__remove">Remove</button>
      </div>
    `;

    // Keep it OUT of label/click-proxy subtree by appending to wrapper
    const label = wrapper.querySelector('label.bold_option_title') || wrapper.querySelector('label');

    if (label && typeof label.insertAdjacentElement === 'function') {
        // Insert chip immediately after the label (as a sibling)
        label.insertAdjacentElement('afterend', chip);
    } else {
        // Fallback: append at end of wrapper
        wrapper.appendChild(chip);
    }


    // Prevent delegated click handlers from opening the file picker
    chip.addEventListener('click', (e) => {
      e.stopPropagation();
    });

    return chip;
  }

  function _syncFromInput(input) {
    const wrapper = _getWrapper(input);
    if (!wrapper) return;

    const chip = _ensureChip(input);
    if (!chip) return;

    const img = chip.querySelector('.bold-file-chip__thumb');
    const nameEl = chip.querySelector('.bold-file-chip__name');
    const removeBtn = chip.querySelector('.bold-file-chip__remove');

    const file = input.files && input.files[0] ? input.files[0] : null;

    // No file selected → show native UI, hide chip
    if (!file) {
      wrapper.classList.remove(CLASS.hasFile);
      chip.classList.remove(CLASS.chipActive);

      if (nameEl) {
        nameEl.textContent = '';
        nameEl.title = '';
      }

      if (img) {
        _revokeObjectUrl(img);
        img.removeAttribute('src');
        img.style.display = '';
      }

      return;
    }

    // File selected → hide native UI, show chip
    wrapper.classList.add(CLASS.hasFile);
    chip.classList.add(CLASS.chipActive);

    if (nameEl) {
      nameEl.textContent = file.name;
      nameEl.title = file.name;
    }

    // Thumbnail only for images
    if (img) {
      _revokeObjectUrl(img);

      if (file.type && file.type.indexOf('image/') === 0) {
        const url = URL.createObjectURL(file);
        img.dataset.objectUrl = url;
        img.src = url;
        img.style.display = '';
      } else {
        img.removeAttribute('src');
        img.style.display = 'none';
      }
    }

    // Remove handler (bound once)
    if (removeBtn && !removeBtn.dataset.bound) {
      removeBtn.dataset.bound = '1';
      removeBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();

        // Clear facade file flag
        const label = _getLabelFromInput(input);
        const facade = _getFacade();
        try {
          if (label && facade && facade.actions && typeof facade.actions.updateOption === 'function') {
            facade.actions.updateOption(label, null);
          }
        } catch (err) {
          // soft-fail
        }

        // Clear the actual input
        try {
          input.value = '';
        } catch (err) {}

        // Sync UI immediately
        _syncFromInput(input);

        // Emit native change so downstream logic sees the update
        try {
          input.dispatchEvent(new Event('change', { bubbles: true }));
        } catch (err) {}
      });
    }
  }

  function enhance(input) {
    if (!input) return;
    if (input.getAttribute('data-bold-file-enhanced') === '1') return;
    input.setAttribute('data-bold-file-enhanced', '1');

    _ensureChip(input);

    input.addEventListener('change', () => _syncFromInput(input));
    _syncFromInput(input);
  }

  function scan(root) {
    const scope = root || document;
    const inputs = scope.querySelectorAll(SELECTORS.fileInput);
    inputs.forEach(enhance);
  }

  function _scheduleScan() {
    if (_scanScheduled) return;
    _scanScheduled = true;
    requestAnimationFrame(() => {
      _scanScheduled = false;
      scan();
    });
  }

  function _setupObserver() {
    if (_observer) return;

    const root =
      document.querySelector('product-form') ||
      document.querySelector('form[action*="/cart/add"]') ||
      document.body;

    _observer = new MutationObserver((mutations) => {
      for (const m of mutations) {
        if (m.type === 'childList' && m.addedNodes && m.addedNodes.length) {
          _scheduleScan();
          return;
        }
        if (m.type === 'attributes' && m.attributeName === 'class') {
          _scheduleScan();
          return;
        }
      }
    });

    _observer.observe(root, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class'],
    });
  }

  window.BoldFileUploadUI = { scan, enhance };

  document.addEventListener('hub:options:ready', () => {
    _setupObserver();
    _scheduleScan();
  });

  document.addEventListener('hub:price:recalculated', _scheduleScan);
  document.addEventListener('hub:bold:variant_changed', _scheduleScan);

  if (document.readyState !== 'loading') {
    _setupObserver();
    _scheduleScan();
  } else {
    document.addEventListener('DOMContentLoaded', () => {
      _setupObserver();
      _scheduleScan();
    });
  }
})();
